// Demo data for the /features/bookings page: the availability payload the API
// sends the real Book page (BookingPageBody and the grid under it), a viewer,
// the sheet's quote and booking, and the admin pieces (who can book, a set of
// booking rules), all for a made-up club so the page runs the real components
// on static props and never calls the API. Nothing here is a real club,
// player or booking.
import {AUDIENCE} from '~/utils/bookings';

export const BOOKING_DEMO_CLUB = Object.freeze({
  id: '00000000-0000-0000-0000-000000000001',
  name: 'Riverside Racquets Club',
  players: [
    {id: 'p-dana', userId: 'u-dana', fullName: 'Dana Reed'},
    {id: 'p-sam', userId: 'u-sam', fullName: 'Sam Taylor'},
    {id: 'p-priya', userId: 'u-priya', fullName: 'Priya Khan'},
    {id: 'p-alex', userId: 'u-alex', fullName: 'Alex Morgan'},
    {id: 'p-mo', userId: 'u-mo', fullName: 'Mo Hassan'},
  ],
});

const SPORTS = [
  {id: 'sport-tennis', name: 'Tennis', venueNoun: 'court', venueNounPlural: 'courts'},
  {id: 'sport-darts', name: 'Darts', venueNoun: 'board', venueNounPlural: 'boards'},
];

const GROUPS = [
  {id: 'g-courts', name: 'Courts', slotMinutes: 60, cancelCutoffMinutes: 60},
  {id: 'g-boards', name: 'Boards', slotMinutes: 30, cancelCutoffMinutes: 30},
];

const AREAS = [
  {id: 'a-c1', name: 'Court 1', groupId: 'g-courts', canBeBooked: true, sportIds: ['sport-tennis'], sort: 0},
  {id: 'a-c2', name: 'Court 2', groupId: 'g-courts', canBeBooked: true, sportIds: ['sport-tennis'], sort: 1},
  {id: 'a-c3', name: 'Court 3', groupId: 'g-courts', canBeBooked: true, sportIds: ['sport-tennis'], sort: 2},
  {id: 'a-b1', name: 'Board 1', groupId: 'g-boards', canBeBooked: true, sportIds: ['sport-darts'], sort: 3},
  {id: 'a-b2', name: 'Board 2', groupId: 'g-boards', canBeBooked: true, sportIds: ['sport-darts'], sort: 4},
];

/** yyyy-MM-dd of a day `offset` days from today, on the viewer's own clock (the demo club runs on it). */
export function bookingDemoDate(offset = 0, now = new Date()) {
  const d = new Date(now);
  d.setDate(d.getDate() + offset);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const atMinutes = (date, minutes) => {
  const d = new Date(`${date}T00:00:00`);
  d.setMinutes(minutes);
  return d.toISOString();
};
const label = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

// A deterministic spread of what a day looks like: a few bookings, one of the
// viewer's own, a match, a frozen court and a matches-only evening on Board 2.
const courtStatus = (dayIndex, hour, areaIndex) => {
  if (areaIndex === 2 && dayIndex === 1) return {status: 'frozen', label: 'Maintenance'};
  const seed = (dayIndex * 7 + hour * 3 + areaIndex * 5) % 11;
  if (dayIndex === 0 && hour === 19 && areaIndex === 0) return {status: 'mine', mine: true, bookingId: 'b-mine'};
  if (seed === 0 || seed === 4) return {status: 'booked', label: 'Booked', bookingId: `b-${dayIndex}-${hour}-${areaIndex}`, kind: 'Play'};
  if (seed === 7 && hour >= 18) return {status: 'match', label: 'Match', bookingId: `m-${dayIndex}-${hour}-${areaIndex}`, kind: 'Match'};
  if (seed === 9 && hour < 10) return {status: 'booked', label: 'Club', kind: 'Admin', bookingId: `c-${dayIndex}-${hour}-${areaIndex}`};
  return {status: 'free'};
};
const boardStatus = (dayIndex, minutes, areaIndex) => {
  if (areaIndex === 1 && minutes >= 19 * 60 && dayIndex % 2 === 0) return {status: 'matchesOnly', label: 'Matches only'};
  const seed = (dayIndex * 5 + minutes / 30 + areaIndex * 3) % 9;
  if (seed === 2) return {status: 'booked', label: 'Booked', bookingId: `bb-${dayIndex}-${minutes}-${areaIndex}`, kind: 'Play'};
  return {status: 'free'};
};

const courtSlots = (date, dayIndex) => Array.from({length: 14}, (_, i) => {
  const hour = 8 + i;
  const matchesOnly = dayIndex === 3 && hour >= 18;
  return {
    startsAt: atMinutes(date, hour * 60), endsAt: atMinutes(date, hour * 60 + 60), label: label(hour * 60),
    kind: matchesOnly ? 'MatchesOnly' : 'BookingNeeded', bookable: !matchesOnly, openPlay: false,
    areas: AREAS.filter((a) => a.groupId === 'g-courts').map((a, areaIndex) => ({
      areaId: a.id, ...(matchesOnly ? {status: 'matchesOnly', label: 'Matches only'} : courtStatus(dayIndex, hour, areaIndex)),
    })),
  };
});
const boardSlots = (date, dayIndex) => Array.from({length: 12}, (_, i) => {
  const minutes = 17 * 60 + i * 30;
  return {
    startsAt: atMinutes(date, minutes), endsAt: atMinutes(date, minutes + 30), label: label(minutes),
    kind: 'BookingNeeded', bookable: true, openPlay: false,
    areas: AREAS.filter((a) => a.groupId === 'g-boards').map((a, areaIndex) => ({areaId: a.id, ...boardStatus(dayIndex, minutes, areaIndex)})),
  };
});

/** Two weeks from today: fourteen days the viewer can book, the days after that beyond the window. */
export function bookingDemoDays(now = new Date()) {
  return Array.from({length: 21}, (_, dayIndex) => {
    const date = bookingDemoDate(dayIndex, now);
    const beyond = dayIndex > 14;
    return {
      date, beyondWindow: beyond, opensAt: beyond ? atMinutes(bookingDemoDate(1, now), 0) : null, note: '',
      groups: [
        {groupId: 'g-courts', note: '', slots: courtSlots(date, dayIndex).map((s) => (beyond ? {...s, areas: s.areas.map((x) => ({areaId: x.areaId, status: 'notYetOpen'}))} : s)), overrides: []},
        {groupId: 'g-boards', note: '', slots: boardSlots(date, dayIndex).map((s) => (beyond ? {...s, areas: s.areas.map((x) => ({areaId: x.areaId, status: 'notYetOpen'}))} : s)), overrides: []},
      ],
    };
  });
}

/** A Senior member of the club, on the Senior row of the rules table below: 3 h at a time, 14 days ahead, 4 at once, £4.00 a court slot, boards free. */
export const bookingDemoViewer = Object.freeze({
  signedIn: true, canBook: true, isAdmin: false, currency: 'gbp', daysAhead: 14, maxActiveBookings: 4, activeBookings: 1,
  guestsAllowed: true, guestPriceMinor: 200,
  byGroup: {
    'g-courts': {canBook: true, priceMinor: 400, maxDurationMinutes: 180, daysAhead: 14, maxActiveBookings: 4, activeBookings: 1},
    'g-boards': {canBook: true, priceMinor: 0, maxDurationMinutes: 180, daysAhead: 14, maxActiveBookings: 4, activeBookings: 1},
  },
});

/** The same club as its admin: no limits, any length, a note where a member rule is passed. */
export const bookingDemoAdminViewer = Object.freeze({
  ...bookingDemoViewer, isAdmin: true, maxActiveBookings: null, activeBookings: 0, daysAhead: 60,
  byGroup: {
    'g-courts': {canBook: true, priceMinor: 400, maxDurationMinutes: null, allDay: true, daysAhead: 60, maxActiveBookings: null, activeBookings: 0},
    'g-boards': {canBook: true, priceMinor: 0, maxDurationMinutes: null, allDay: true, daysAhead: 60, maxActiveBookings: null, activeBookings: 0},
  },
});

/** The availability payload (BookingPageBody's `availability`, the grid's source). */
export function bookingDemoAvailability({viewer = bookingDemoViewer, now = new Date()} = {}) {
  return {
    clubId: BOOKING_DEMO_CLUB.id, clubName: BOOKING_DEMO_CLUB.name, bookingsEnabled: true, bookingsActive: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    areaNoun: 'court or board', areaNounPlural: 'courts and boards', availabilityTitle: 'Court and board availability', bookLabel: 'Book a court or board',
    sports: SPORTS, groups: GROUPS, areas: AREAS, viewer, dayList: bookingDemoDays(now), from: bookingDemoDate(0, now),
  };
}

/** The sheet's quote for a pick: the price per slot of the area's own rules, times the slots, plus guest fees. */
export function bookingDemoQuote(body, {viewer = bookingDemoViewer} = {}) {
  const area = AREAS.find((a) => a.id === body.playingAreaId);
  const group = GROUPS.find((g) => g.id === area?.groupId) ?? GROUPS[0];
  const allowance = viewer.byGroup?.[group.id] ?? viewer;
  const slots = Math.max(1, body.slots ?? 1);
  const priceMinor = (allowance.priceMinor ?? 0) * slots;
  const guests = (body.participants ?? []).filter((p) => !p.userId).length;
  const guestFeesMinor = guests * (viewer.guestPriceMinor ?? 0);
  const overLength = viewer.isAdmin && slots * group.slotMinutes > 120;
  return {
    allowed: true, currency: 'gbp', priceMinor, guestFeesMinor, totalMinor: priceMinor + guestFeesMinor, needsCard: false,
    cancelLine: group.cancelCutoffMinutes === 30 ? 'Cancel up to 30 minutes before' : 'Cancel up to 1 hour before',
    chips: overLength ? ['longer than allowed'] : [],
  };
}

/** The booking the sheet gets back: confirmed, in the club's own words. */
export function bookingDemoBook(body) {
  const area = AREAS.find((a) => a.id === body.playingAreaId);
  const group = GROUPS.find((g) => g.id === area?.groupId) ?? GROUPS[0];
  const start = new Date(body.startsAt);
  const end = new Date(start.getTime() + Math.max(1, body.slots ?? 1) * group.slotMinutes * 60_000);
  const clock = (d) => `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  const line = `${area?.name ?? 'Court 1'} · ${clock(start)}–${clock(end)}`;
  return {id: 'demo-booking', outcome: 'confirmed', status: 'Confirmed', line, message: `Booked: ${line}. This is a demo, so nothing was really booked.`};
}

/** Home's Book entry: one club that takes bookings (ONE big button). */
export const bookingDemoHomeClubs = Object.freeze([
  {id: BOOKING_DEMO_CLUB.id, name: BOOKING_DEMO_CLUB.name, bookLabel: 'Book a court or board'},
]);

/** Home's Book entry for someone in two clubs that take bookings (a Book card, one row each). */
export const bookingDemoHomeClubsTwo = Object.freeze([
  ...bookingDemoHomeClubs,
  {id: '00000000-0000-0000-0000-000000000002', name: 'Hillside Tennis Club', bookLabel: 'Book a court'},
]);

/** The membership types the Who can book table lists (live ones, with their counts). */
export const bookingDemoMembershipTypes = Object.freeze([
  {id: 'mt-senior', name: 'Senior membership', currentCount: 148},
  {id: 'mt-junior', name: 'Junior membership', currentCount: 63},
  {id: 'mt-summer', name: 'Summer pass', currentCount: 27},
]);

/** One set of booking rules as the admin's Who can book table takes it (WhoCanBookTable's `group`). */
export const bookingDemoRulesGroup = Object.freeze({
  id: 'g-courts', name: 'Courts', slotMinutes: 60, cancelCutoffMinutes: 60, version: 1,
  hoursLine: 'Mon–Fri 8:00–22:00 · Sat–Sun 9:00–18:00', dayOverridesComingUp: 2,
  playingAreas: AREAS.filter((a) => a.groupId === 'g-courts').map((a) => ({id: a.id, name: a.name, canBeBooked: a.canBeBooked, nounPlural: 'courts'})),
  windows: [
    {days: [0, 1, 2, 3, 4], startLocal: '08:00', endLocal: '22:00', kind: 'BookingNeeded'},
    {days: [5, 6], startLocal: '09:00', endLocal: '18:00', kind: 'BookingNeeded'},
    {days: [3], startLocal: '18:00', endLocal: '22:00', kind: 'MatchesOnly'},
  ],
  whoCanBook: [
    {audience: AUDIENCE.noMembership, label: 'No membership', canBook: true, maxDurationMinutes: 120, daysAhead: 7, maxActiveBookings: 2, priceMinor: 600},
    {audience: AUDIENCE.membershipType, membershipTypeId: 'mt-senior', label: 'Senior membership', canBook: true, maxDurationMinutes: 180, daysAhead: 14, maxActiveBookings: 4, priceMinor: 400},
    {audience: AUDIENCE.membershipType, membershipTypeId: 'mt-junior', label: 'Junior membership', canBook: true, maxDurationMinutes: null, daysAhead: null, maxActiveBookings: null, priceMinor: 300},
    {audience: AUDIENCE.membershipType, membershipTypeId: 'mt-summer', label: 'Summer pass', canBook: true, maxDurationMinutes: null, daysAhead: null, maxActiveBookings: null, priceMinor: 0},
    {audience: AUDIENCE.visitor, label: 'Visitors', canBook: true, maxDurationMinutes: 60, daysAhead: 3, priceMinor: 800},
    {audience: AUDIENCE.guest, label: 'Guests', canBook: true, priceMinor: 200},
  ],
});
